import {createSlice} from '@reduxjs/toolkit'
import {client} from "../../api/client";

export const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: undefined,
    pendingEmail: null,
    awaitingCode: false,
    isLoading: false,
    errors: {
      login: null,
      profileUpdate: null,
    },
  },
  reducers: {
    setLoading: state => {
      state.isLoading = true
    },
    setPendingLogin: (state, action) => {
      state.pendingEmail = action.payload.email
      state.awaitingCode = true
      state.user = null
      state.isLoading = false
      state.errors.login = null
    },
    finishLogin: state => {
      state.pendingEmail = null
      state.awaitingCode = false
      state.errors.login = null
    },
    setUser: (state, action) => {
      let user = action.payload;
      console.log(`updating user ${user.email}`)
      state.user = user
      state.isLoading = false
    },
    logout: state => {
      console.log("logging out")
      state.user = null
      state.pendingEmail = null
      state.awaitingCode = false
      state.isLoading = false
      localStorage.removeItem("auth/token")
    },
    loginError: (state, action) => {
      state.isLoading = false
      state.errors.login = action.payload
    },
    profileUpdateError: (state, action) => {
      state.isLoading = false
      state.errors.profileUpdate = action.payload
    },
  }
})

const {
  setLoading,
  setPendingLogin,
  finishLogin,
  setUser,
  loginError,
  profileUpdateError,
  logout,
} = authSlice.actions

const normaliseAuthErrors = (data, fallback) => {
  if (data && Array.isArray(data.errors)) {
    return data.errors.reduce((errors, error) => {
      const field = error.param || 'non_field_errors'
      errors[field] = [...(errors[field] || []), error.message || fallback]
      return errors
    }, {})
  }

  if (data && typeof data === 'object')
    return data

  return {non_field_errors: [fallback]}
}

const hasPendingCodeFlow = data => data?.data?.flows?.some(
    flow => flow.id === 'login_by_code' && flow.is_pending === true
)

export const startEmailLogin = email => async dispatch => {
  console.log(`requesting login code for ${email}`)
  dispatch(setLoading())
  try {
    await client.post('auth/headless/browser/start/', {email})
    dispatch(loginError({non_field_errors: ['The server did not start an email login. Please try again.']}))
  } catch (e) {
    if (e.response?.status === 401 && hasPendingCodeFlow(e.data)) {
      dispatch(setPendingLogin({email}))
      return
    }

    dispatch(loginError(normaliseAuthErrors(
        e.data,
        'We could not send a login code. Please try again.',
    )))
  }
}

export const confirmEmailCode = code => async dispatch => {
  dispatch(setLoading())
  try {
    const response = await client.post(
        'auth/headless/browser/v1/auth/code/confirm',
        {code},
    )
    if (!response.meta?.is_authenticated)
      throw new Error('The server did not confirm the email login.')

    dispatch(finishLogin())
    await dispatch(fetchUser())
  } catch (e) {
    const status = e.response?.status
    if (status === 409 || status === 410 || status === 401) {
      await dispatch(performLogout())
      dispatch(loginError({non_field_errors: [
        status === 410
            ? 'Your login session has expired. Please request a new code.'
            : 'That code can no longer be used. Please request a new one.',
      ]}))
      return
    }

    dispatch(loginError(normaliseAuthErrors(
        e.data,
        status === 429
            ? 'Too many attempts. Please wait before trying again.'
            : 'The code is invalid. Check the email and try again.',
    )))
  }
}

export const performLogout = () => async dispatch => {
  dispatch(logout())
  try {
    await client.delete('auth/headless/browser/v1/auth/session')
  } catch (e) {
    // The local session must be removed even if the server already rejected it.
  }
}

export const restartEmailLogin = performLogout

export const restoreSession = () => async dispatch => {
  dispatch(setLoading())
  try {
    const response = await client.get('auth/headless/browser/v1/auth/session')
    if (!response.meta?.is_authenticated)
      throw new Error('Session is not authenticated')
    await dispatch(fetchUser())
  } catch (e) {
    dispatch(logout())
  }
}

export const fetchUser = () => async dispatch => {
  console.log("fetching user")
  dispatch(setLoading())
  try {
    let user = await client.get('rest-auth/user/?version=13')
    console.log(`current user ${user.email}`)
    dispatch(setUser(user))
  } catch (e) {
    dispatch(logout())
  }
};

export const performProfileUpdate = (name) => async dispatch => {
  console.log("fetching user")
  dispatch(setLoading())
  try {
    const user = await client.put('rest-auth/user/?version=13', {name})
    dispatch(setUser(user))
  } catch (e) {
    dispatch(profileUpdateError(e.data))
  }
}

export const selectUser = state => state.auth.user

export const selectAuthenticated = state => {
  let user = selectUser(state)
  if (user === undefined)
    return undefined;
  return user !== null;
}

export const selectIsLoading = state => state.auth.isLoading
export const selectLoginErrors = state => state.auth.errors.login
export const selectPendingEmail = state => state.auth.pendingEmail
export const selectAwaitingCode = state => state.auth.awaitingCode
export const selectProfileUpdateErrors = state => state.auth.errors.profileUpdate

export default authSlice.reducer
